import { FeatureKeys, Enums } from '@/services/dropdown-enums';

const FeatureService = {
  /**
   * Resolves active features for the user, applying role-based overrides first.
   * If the user is in a strict scheduling role, they are limited strictly to the appointment scheduling feature.
   * Otherwise, returns the array of features resolved from their profile.
   */
  resolveActiveFeatures(user) {
    const isStrictSchedulingRole =
      user?.RoleType === Enums.Roles.SchedulingUser ||
      user?.RoleType === Enums.Roles.SchedulingTeamManager;

    if (isStrictSchedulingRole) {
      return [FeatureKeys.APPOINTMENT_SCHEDULING];
    }

    return Array.from(this.resolveFeatures(user));
  },

  /**
   * Normalizes the raw features[] array from API into a Set<string>.
   * 
   * Handles all backend variations:
   *   - "Appointment Scheduling" → "appointment_scheduling"
   *   - "appointmentScheduling"  → "appointment_scheduling"  
   *   - "appointment_scheduling" → "appointment_scheduling" (already normalized)
   *   - "CRM"                    → "crm"
   * 
   * Algorithm:
   *   1. Trim whitespace
   *   2. Insert underscore before uppercase letters (camelCase → snake_case)
   *   3. Replace spaces with underscores
   *   4. Collapse multiple underscores
   *   5. Lowercase everything
   */
  resolveFeatures(userData) {
    if (!userData) {
      return new Set();
    }

    let rawFeatures = [];

    if (userData instanceof Set) {
      rawFeatures = Array.from(userData);
    } else if (Array.isArray(userData)) {
      rawFeatures = userData;
    } else if (typeof userData === 'object') {
      if (Array.isArray(userData.features)) {
        rawFeatures = userData.features;
      } else if (Array.isArray(userData.UserFeatures)) {
        rawFeatures = userData.UserFeatures;
      } else if (userData.features instanceof Set) {
        rawFeatures = Array.from(userData.features);
      } else if (userData.UserFeatures instanceof Set) {
        rawFeatures = Array.from(userData.UserFeatures);
      }
    }

    if (!Array.isArray(rawFeatures)) {
      return new Set();
    }

    return new Set(
      rawFeatures.map(f => {
        if (typeof f !== 'string') return '';
        return f.trim()
         .replace(/([a-z])([A-Z])/g, '$1_$2')  // camelCase → snake_case
         .replace(/\s+/g, '_')                   // spaces → underscores
         .replace(/_+/g, '_')                    // collapse multiple underscores
         .toLowerCase();
      }).filter(Boolean)
    );
  },
  
  hasFeature(features, featureKey) {
    if (!(features instanceof Set)) return false;
    // CRM is the master feature that unlocks all CRM/SmartHub modules.
    // If no features are defined, default to full access (treated as CRM user).
    if (features.size === 0 || features.has(FeatureKeys.CRM)) return true;
    return features.has(featureKey);
  },

  /**
   * Batch check: does the user have ALL of the listed features?
   */
  hasAllFeatures(features, featureKeys) {
    return featureKeys.every(key => this.hasFeature(features, key));
  },
  
  /**
   * Batch check: does the user have ANY of the listed features?
   */
  hasAnyFeature(features, featureKeys) {
    return featureKeys.some(key => this.hasFeature(features, key));
  },
  
  /**
   * Backward compat: derive a synthetic "plan" from features.
   * Uses FeatureKeys enum internally — no raw strings.
   *
   * Mapping:
   *   features has FeatureKeys.CRM                            →  'pro'
   *   features has ONLY FeatureKeys.APPOINTMENT_SCHEDULING    →  'basic'
   *   features is empty                                        →  'pro' (default)
   * 
   * @deprecated — will be removed after full migration (Phase 4)
   */
  derivePlan(features) {
    if (!(features instanceof Set) || features.size === 0) return 'pro';
    if (this.hasFeature(features, FeatureKeys.CRM)) return 'pro';
    if (this.hasFeature(features, FeatureKeys.APPOINTMENT_SCHEDULING)) return 'basic';
    return 'pro';
  },
};

export default FeatureService;
